/**
 * DeleteRoleModal Component
 *
 * Modal for confirming role deletion with code verification.
 */

import React, { useState } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { useDeleteRole } from '../hooks/useDeleteRole';
import { useAuth } from '../../../contexts/AuthContext';
import type { Role } from '../../../types/role.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface DeleteRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  role: Role;
  onSuccess?: () => void;
}

/**
 * DeleteRoleModal component
 */
export const DeleteRoleModal: React.FC<DeleteRoleModalProps> = ({
  isOpen,
  onClose,
  role,
  onSuccess,
}) => {
  const { tenant } = useAuth();
  const tenantId = tenant?.tenantId || '';
  const [confirmationCode, setConfirmationCode] = useState<string>('');

  // Delete mutation
  const deleteMutation = useDeleteRole(tenantId);

  const handleClose = () => {
    if (!deleteMutation.isPending) {
      setConfirmationCode('');
      deleteMutation.reset();
      onClose();
    }
  };

  const handleDelete = async () => {
    if (confirmationCode === role.roleCode) {
      try {
        await deleteMutation.mutateAsync(role.roleId);
        setConfirmationCode('');
        onSuccess?.();
        onClose();
      } catch (error) {
        // Error is handled by the mutation
        console.error('Failed to delete role:', error);
      }
    }
  };

  const isCodeValid = confirmationCode === role.roleCode;
  const assignedUsers = role.users?.length || 0;
  const assignedGroups = role.groups?.length || 0;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>
        <span className="text-red-600">Delete Role</span>
      </Modal.Header>

      <Modal.Body>
        <div className="space-y-4">
          {/* Error Alert */}
          {deleteMutation.error && (
            <Alert variant="error">
              {getErrorMessage(deleteMutation.error)}
            </Alert>
          )}

          {/* Warning */}
          <Alert variant="warning">
            <div className="space-y-2">
              <p className="font-semibold">This action cannot be undone!</p>
              <p className="text-sm">
                Deleting this role will permanently remove:
              </p>
              <ul className="text-sm list-disc list-inside ml-2">
                <li>The role configuration and permissions</li>
                <li>All user assignments ({assignedUsers} user{assignedUsers !== 1 ? 's' : ''})</li>
                <li>All group assignments ({assignedGroups} group{assignedGroups !== 1 ? 's' : ''})</li>
              </ul>
            </div>
          </Alert>

          {/* Role Info */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div>
              <span className="text-sm font-medium text-gray-700">Role Name:</span>
              <p className="text-gray-900 font-semibold">{role.roleName}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Role Code:</span>
              <p className="text-gray-900 font-mono bg-gray-200 px-2 py-1 rounded inline-block mt-1">
                {role.roleCode}
              </p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Permissions:</span>
              <p className="text-gray-900">{role.permissions?.length || 0} permission(s)</p>
            </div>
          </div>

          {/* Confirmation Input */}
          <div>
            <Input
              label={
                <span>
                  Type <code className="bg-gray-200 px-1 rounded font-mono text-sm">{role.roleCode}</code> to confirm
                </span>
              }
              placeholder="Enter role code"
              value={confirmationCode}
              onChange={(e) => setConfirmationCode(e.target.value)}
              disabled={deleteMutation.isPending}
              autoComplete="off"
            />
            {confirmationCode && !isCodeValid && (
              <p className="mt-1 text-sm text-red-600">
                Role code does not match
              </p>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={!isCodeValid || deleteMutation.isPending}
            loading={deleteMutation.isPending}
          >
            Delete Role
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
